import { Link } from 'react-router-dom';

export default function Home() {
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center bg-gradient-to-br from-slate-50 to-white overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-black/5 via-transparent to-white/50"></div>
        
        <div className="container mx-auto px-6 lg:px-12 py-20 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left Content */}
            <div className="space-y-8">
              <div className="inline-block">
                <span className="text-sm font-semibold tracking-wider text-slate-600 uppercase">Association d'intérêt général</span>
              </div>
              
              <h1 className="text-5xl lg:text-7xl font-bold leading-tight">
                <span className="text-slate-900">Former, Accompagner,</span>
                <br />
                <span className="text-orange-500 italic">Émanciper</span>
              </h1>
              
              <p className="text-xl text-slate-600 leading-relaxed max-w-xl">
                PULSAR FC œuvre pour l'insertion socioprofessionnelle, la citoyenneté positive et l'accompagnement des publics vulnérables à travers des formations civiques et un accompagnement humain de qualité.
              </p>
              
              <div className="flex flex-wrap gap-4">
                <Link 
                  to="/contact" 
                  className="px-10 py-4 bg-slate-900 text-white rounded-full font-semibold hover:bg-slate-800 transition-all duration-300 shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
                >
                  Nous contacter
                </Link>
                <Link 
                  to="/nos-actions" 
                  className="px-10 py-4 border-2 border-orange-500 text-orange-500 rounded-full font-semibold hover:bg-orange-50 transition-all duration-300 whitespace-nowrap cursor-pointer"
                >
                  Découvrir nos actions
                </Link>
              </div>
            </div>
            
            {/* Right Image */}
            <div className="relative">
              <div className="rounded-3xl overflow-hidden shadow-2xl">
                <img 
                  src="https://public.readdy.ai/ai/img_res/f65750cd494a7c335b6a3e1baf1028b6.jpg"
                  alt="Formation et accompagnement PULSAR FC"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -bottom-6 -left-6 w-32 h-32 bg-orange-500 rounded-full opacity-20 blur-3xl"></div>
              <div className="absolute -top-6 -right-6 w-40 h-40 bg-slate-900 rounded-full opacity-10 blur-3xl"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Partenaires institutionnels */}
      <section className="py-16 bg-slate-50">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-slate-800 mb-4">
              Nos références institutionnelles
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              PULSAR FC travaille en collaboration avec les institutions publiques pour garantir la qualité et la légitimité de ses actions de formation civique et d'insertion professionnelle.
            </p>
          </div>
          <div className="flex justify-center items-center">
            <div className="bg-white p-8 rounded-lg shadow-md">
              <img 
                src="https://static.readdy.ai/image/f2824006b8e214af3bf77ca05a5a0829/67ae603801ce349ed5011afa04c42dbd.jpeg"
                alt="Préfecture de la Région d'Île-de-France"
                className="h-32 w-auto object-contain"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Nos Domaines d'Action */}
      <section className="py-24 bg-slate-50">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="text-center mb-16">
            <span className="text-sm font-semibold tracking-wider text-orange-500 uppercase mb-4 block">Notre expertise</span>
            <h2 className="text-4xl lg:text-5xl font-bold text-slate-900 font-serif">Nos domaines d'action</h2>
          </div>
          
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Card 1 - Formation Civique */}
            <div className="bg-slate-900 rounded-3xl p-10 text-white space-y-6 hover:scale-105 transition-transform duration-300">
              <div className="w-16 h-16 bg-slate-700 rounded-full flex items-center justify-center">
                <i className="ri-book-open-line text-3xl"></i>
              </div>
              <h3 className="text-3xl font-bold">Formation Civique</h3>
              <p className="text-slate-300 text-lg leading-relaxed">
                Formation en langue française (FLE), citoyenneté positive, laïcité et Valeurs de la République pour favoriser l'intégration et l'insertion sociale.
              </p>
            </div>
            
            {/* Card 2 - Sensibilisation Numérique */}
            <div className="relative rounded-3xl overflow-hidden group hover:scale-105 transition-transform duration-300">
              <img 
                src="https://readdy.ai/api/search-image?query=adults%20learning%20about%20digital%20literacy%20and%20social%20media%20awareness%20in%20modern%20training%20room%20with%20computers%20and%20tablets%20showing%20responsible%20internet%20usage%20education&width=600&height=700&seq=digital-awareness-card&orientation=portrait"
                alt="Sensibilisation numérique"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent"></div>
              <div className="absolute top-6 left-6">
                <span className="px-4 py-2 bg-white/20 backdrop-blur-md rounded-full text-white text-sm font-semibold flex items-center gap-2 w-fit">
                  <i className="ri-shield-check-line"></i>
                  Prévention
                </span>
              </div>
              <div className="absolute bottom-6 left-6 right-6 text-white">
                <h3 className="text-3xl font-bold mb-3">Sensibilisation Numérique</h3>
                <p className="text-white/90 text-lg">Formation aux dangers des fake news, réseaux sociaux et surconsommation des écrans.</p>
              </div>
            </div>
            
            {/* Card 3 - Accompagnement Professionnel */}
            <div className="bg-white rounded-3xl p-10 space-y-6 shadow-lg hover:shadow-2xl transition-all duration-300">
              <h3 className="text-3xl font-bold text-slate-900 font-serif">Accompagnement Professionnel</h3>
              <p className="text-slate-600 text-lg leading-relaxed">
                Développement des compétences, conseil et services pour demandeurs d'emploi, bénéficiaires du RSA et publics vulnérables.
              </p>
              <div className="pt-4">
                <img 
                  src="https://readdy.ai/api/search-image?query=professional%20career%20counseling%20session%20with%20advisor%20helping%20job%20seeker%20in%20bright%20office%20showing%20support%20guidance%20and%20professional%20development%20planning&width=500&height=350&seq=career-support-illustration&orientation=landscape"
                  alt="Accompagnement professionnel"
                  className="w-full h-48 object-cover rounded-2xl"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Impact & Valeurs */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="max-w-5xl mx-auto text-center space-y-12">
            <div className="rounded-3xl overflow-hidden shadow-2xl">
              <img 
                src="https://readdy.ai/api/search-image?query=diverse%20community%20members%20working%20together%20in%20collaborative%20workshop%20showing%20unity%20solidarity%20social%20impact%20and%20positive%20citizenship%20values%20in%20bright%20welcoming%20space&width=1400&height=700&seq=impact-values-banner&orientation=landscape"
                alt="Impact social PULSAR FC"
                className="w-full h-96 object-cover"
              />
            </div>
            
            <div className="space-y-6">
              <h2 className="text-4xl lg:text-5xl font-bold leading-tight">
                <span className="text-slate-900">Construire une société plus </span>
                <span className="text-slate-400 italic">inclusive</span>
                <span className="text-slate-900">, plus </span>
                <span className="text-slate-400 italic">solidaire</span>
                <span className="text-slate-900"> et plus </span>
                <span className="text-slate-400 italic">citoyenne</span>
              </h2>
              
              <Link 
                to="/qui-sommes-nous" 
                className="inline-block px-12 py-4 bg-slate-900 text-white rounded-full font-semibold hover:bg-slate-800 transition-all duration-300 shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
              >
                Découvrir notre mission
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Publics Accompagnés */}
      <section className="py-24 bg-slate-50">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="mb-16">
            <span className="inline-flex items-center gap-2 text-sm font-semibold text-slate-600 mb-4">
              <span className="w-2 h-2 bg-slate-900 rounded-full"></span>
              À qui s'adressent nos actions
            </span>
            <h2 className="text-4xl lg:text-5xl font-bold text-slate-900 font-serif">Publics accompagnés</h2>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {/* Public 1 */}
            <div className="bg-white rounded-3xl p-8 space-y-6 hover:shadow-xl transition-all duration-300">
              <div className="space-y-4">
                <h3 className="text-2xl font-bold text-slate-900">Demandeurs d'emploi &amp; Bénéficiaires RSA</h3>
                <p className="text-slate-600 leading-relaxed">
                  Accompagnement personnalisé vers l'insertion professionnelle, développement des compétences et construction de projets professionnels durables.
                </p>
              </div>
              <div className="rounded-2xl overflow-hidden h-48">
                <img 
                  src="https://readdy.ai/api/search-image?query=job%20seeker%20meeting%20with%20employment%20counselor%20in%20professional%20office%20showing%20hope%20determination%20and%20career%20guidance%20support%20for%20professional%20reintegration&width=500&height=400&seq=job-seekers-support&orientation=landscape"
                  alt="Demandeurs d'emploi"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
            
            {/* Public 2 */}
            <div className="bg-white rounded-3xl p-8 space-y-6 hover:shadow-xl transition-all duration-300">
              <div className="space-y-4">
                <h3 className="text-2xl font-bold text-slate-900">Publics Vulnérables &amp; Discriminés</h3>
                <p className="text-slate-600 leading-relaxed">
                  Soutien psychologique, accompagnement social et formation adaptée pour les personnes en situation de fragilité ou victimes de discriminations.
                </p>
              </div>
              <div className="rounded-2xl overflow-hidden h-48">
                <img 
                  src="https://readdy.ai/api/search-image?query=diverse%20group%20of%20vulnerable%20people%20receiving%20compassionate%20support%20and%20counseling%20in%20welcoming%20community%20center%20showing%20empathy%20care%20and%20social%20inclusion&width=500&height=400&seq=vulnerable-populations&orientation=landscape"
                  alt="Publics vulnérables"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
            
            {/* Public 3 */}
            <div className="bg-white rounded-3xl p-8 space-y-6 hover:shadow-xl transition-all duration-300">
              <div className="space-y-4">
                <h3 className="text-2xl font-bold text-slate-900">Institutions &amp; Entreprises</h3>
                <p className="text-slate-600 leading-relaxed">
                  Services de formation, consultation et conseil pour les administrations publiques, parapubliques et le secteur privé.
                </p>
              </div>
              <div className="rounded-2xl overflow-hidden h-48">
                <img 
                  src="https://readdy.ai/api/search-image?query=professional%20business%20meeting%20with%20institutional%20partners%20and%20corporate%20representatives%20in%20modern%20conference%20room%20showing%20collaboration%20partnership%20and%20professional%20consulting&width=500&height=400&seq=institutions-partners&orientation=landscape"
                  alt="Institutions et entreprises"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
          
          <div className="text-center mt-12">
            <Link 
              to="/publics-accompagnes" 
              className="inline-block px-10 py-4 border-2 border-slate-900 text-slate-900 rounded-full font-semibold hover:bg-slate-900 hover:text-white transition-all duration-300 whitespace-nowrap cursor-pointer"
            >
              En savoir plus sur nos publics
            </Link>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-24 bg-gradient-to-br from-orange-50 to-white">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="max-w-4xl mx-auto text-center space-y-8">
            <h2 className="text-4xl lg:text-5xl font-bold text-slate-900">
              Rejoignez notre mission pour une société plus inclusive
            </h2>
            <p className="text-xl text-slate-600 leading-relaxed">
              Que vous soyez une institution, une entreprise ou un particulier, nous sommes à votre écoute pour construire ensemble des projets à impact social positif.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link 
                to="/contact" 
                className="px-12 py-4 bg-orange-500 text-white rounded-full font-semibold hover:bg-orange-600 transition-all duration-300 shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
              >
                Devenir partenaire
              </Link>
              <Link 
                to="/partenariats-impact" 
                className="px-12 py-4 bg-white text-slate-900 rounded-full font-semibold hover:bg-slate-50 transition-all duration-300 shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
              >
                Découvrir nos partenariats
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
